import { Durable, type JsonObject, type Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import type { TypedApi } from "@tento-core/typed-api";
import { DurableHandoffs } from "../../client/durable-handoffs.ts";
import { observedDurable } from "../../client/unobserved-settled.ts";
import { SdkRequestError } from "../../errors.ts";
import { LiveData } from "@tento-core/livedata";
import { ResourceClient } from "@tento-core/resource-client";
import { Tracing } from "@tento-core/tracing";
import { Network } from "../../network.ts";
import { ThemePreferences, writeThemeName } from "../../preferences/theme-preferences.ts";
import type { PreferencesStorage } from "../../preferences/preferences-storage.ts";
import type { SheetHost } from "../../client/sheet-host.ts";
import { Actor, type InvalidationSink, ThemeChanged } from "../../invalidation.ts";
import type { ClientLifecycle } from "../../client/handle-errors.ts";
import { deepFrozen } from "@tento-lona/sheets/internal-sdk";
import { Internals } from "@tento-lona/sheets/internal-ui";

/** `ThemesClient`'s friend record (`Internals`): its constructor registers each instance. */
type ThemesClientInternals = {
  readonly seed: (themes: ThemesClient, theme: Option<ThemesClient.Theme>) => void;
};
const themesInternals = new Internals<ThemesClient, ThemesClientInternals>("ThemesClient");

/**
 * Seed `themes` with the theme the server holds (the initial payload's),
 * silently: `live` holds it and the host applies it, nothing is stored or
 * announced, and it is what a refused `setDurable` takes the choice back
 * to. The host's door (`platformOf(client).themes.seed`); an app chooses a
 * theme through `client.themes.setDurable(theme, actor)`.
 *
 * @internal Not exported from `lib.ts`.
 */
export function seedThemes(themes: ThemesClient, theme: Option<ThemesClient.Theme>): void {
  themesInternals.of(themes).seed(themes, theme);
}

/**
 * The user's theme (`client.themes`). `live` is the read: the applied
 * theme, live. `setDurable(theme, actor)` is the one writer: it applies the
 * theme through the host (`SheetHost.themeBackend`), stores its name locally,
 * persists it and announces `ThemeChanged` stamped with the writer.
 * The server's theme at boot is seeded through the host's door
 * (`platformOf(client).themes.seed(theme)`).
 * `get(name)` loads a theme by name (its JSON, through the client's
 * `fetch`); `peek(name)` reads one already loaded.
 */
export class ThemesClient {
  static readonly #internals: ThemesClientInternals = {
    seed: (themes, theme) => themes.#seed(theme),
  };

  static readonly ACTOR = new Actor("theme-store");
  readonly #preferences: ThemePreferences;
  readonly #host: SheetHost;
  readonly #invalidation: InvalidationSink;
  readonly #persistFn: Option<ThemesClient.Persist>;
  /** The host's static resources (theme JSON), fetched through the client's transport. */
  readonly #resources: ResourceClient;
  readonly #cache = new Map<string, ThemesClient.Theme>();
  #current = new LiveData<Option<ThemesClient.Theme>>(null);
  /** The theme the server holds, as far as this store knows: the seeded
   *  theme, then each accepted `setDurable` — what a refusal takes back to. */
  #confirmed: Option<ThemesClient.Theme> = null;
  /** Issue order of `setDurable`s: the newest choice still standing shows. */
  #issued = 0;
  /** The issue number of the newest accepted `setDurable`. */
  #confirmedIssue = 0;
  /** The `setDurable`s whose server write has not settled, by issue number:
   *  with the confirmed theme, the choices still standing. */
  readonly #outstanding = new Map<number, Option<ThemesClient.Theme>>();
  /** The issue number of the choice on display (`#confirmedIssue` when it is
   *  the confirmed theme): a settle re-shows only when the newest standing
   *  choice is another one. */
  #shownIssue = 0;
  /** The owning client's liveness: `setDurable` and `get` check it. */
  readonly #lifecycle: ClientLifecycle;

  /** `persist`: the server write of a chosen theme (`ThemesClient.Persist`;
   *  the client's is `ThemesClient.persistDurable(api, durable)`), `null` for a
   *  store that only keeps it locally. `lifecycle`: the owning client's
   *  (`LonaSdk.Client.Lifecycle`); after its `dispose`, `setDurable` (which
   *  persists) settles with `LonaSdk.Client.DisposedError` and `get`
   *  (which fetches) throws it, nothing changed. */
  constructor(
    storage: PreferencesStorage,
    host: SheetHost,
    invalidation: InvalidationSink,
    persist: Option<ThemesClient.Persist>,
    fetchFn: Option<typeof fetch>,
    lifecycle: ClientLifecycle,
  ) {
    themesInternals.register(this, ThemesClient.#internals);
    this.#lifecycle = lifecycle;
    this.#preferences = new ThemePreferences(storage);
    this.#host = host;
    this.#invalidation = invalidation;
    this.#persistFn = persist;
    // Per instance, not at module load: the origin is read when the client is
    // built, and theme JSON goes through the client's `fetch` (global when null).
    this.#resources = ResourceClient.currentOrigin().withFetcher((url) =>
      fetchFn != null ? fetchFn(url) : globalThis.fetch(url)
    );
  }

  get live(): LiveData.Readable<Option<ThemesClient.Theme>> {
    return this.#current;
  }

  /** `seedThemes`: the server's theme, shown silently (no store, no announcement). */
  #seed(theme: Option<ThemesClient.Theme>): void {
    this.#confirmed = theme;
    this.#shownIssue = this.#confirmedIssue;
    this.#current.updateSilently(theme);
    this.#host.themeBackend?.apply(theme);
  }

  /**
   * Choose the user's theme, DURABLY: it shows at once — `live` updates,
   * the host's theme backend applies it, the stored theme name is written
   * and `ThemeChanged` is emitted stamped with `actor` — and the server write
   * (`PATCH /api/user/themes`, handed to the client's debounce executor) is
   * the returned `Durable`'s `settled`. `applied` is `Durable.shown(theme)`.
   * `settled` resolves once the execution that carried the write answered
   * without refusing it, and rejects with `SdkRequestError` (boundary
   * `user`, step `update`: `"refused"`, `"unanswered"` or `"malformed"`)
   * once the change was taken back. Whatever order the writes settle in,
   * the newest choice still standing shows — a choice is standing while its
   * write is outstanding, then only if the server accepted it and no newer
   * accepted choice replaced it (the seeded theme counts as accepted). When a
   * settle leaves another choice the newest standing one (a refusal of the
   * shown choice; an older choice accepted after every newer one was
   * refused), it shows and is stored, announced as `ThemesClient.ACTOR`; a
   * newer choice still standing keeps showing and settles on its own. The
   * caller owns `settled`. A durable verb never
   * throws: on a disposed client `settled` rejects with
   * `LonaSdk.Client.DisposedError`, nothing applied (`applied` is
   * `on-settle`). A store with no `persist` settles at once.
   */
  setDurable(theme: Option<ThemesClient.Theme>, actor: Actor): Durable<Option<ThemesClient.Theme>> {
    try {
      this.#lifecycle.assertLive("themes.setDurable");
    } catch (err) {
      return observedDurable(this.#lifecycle, { applied: Durable.ON_SETTLE, settled: Promise.reject(err) });
    }
    const issue = ++this.#issued;
    this.#shownIssue = issue;
    this.#show(theme, actor);
    const persist = this.#persistFn;
    if (persist == null) {
      this.#accept(theme, issue);
      return observedDurable(this.#lifecycle, { applied: Durable.shown(theme), settled: Promise.resolve() });
    }
    this.#outstanding.set(issue, theme);
    const settled = persist(theme).then(
      () => {
        this.#outstanding.delete(issue);
        this.#accept(theme, issue);
        this.#showStanding();
      },
      (err: unknown) => {
        this.#outstanding.delete(issue);
        this.#showStanding();
        throw err;
      },
    );
    return observedDurable(this.#lifecycle, { applied: Durable.shown(theme), settled });
  }

  /** Show the newest choice still standing — an outstanding one, else the
   *  confirmed theme — unless it is already the one on display. */
  #showStanding(): void {
    let standing = this.#confirmedIssue;
    for (const issue of this.#outstanding.keys()) standing = Math.max(standing, issue);
    if (standing === this.#shownIssue) return;
    this.#shownIssue = standing;
    const theme = standing === this.#confirmedIssue ? this.#confirmed : this.#outstanding.get(standing) ?? null;
    this.#show(theme, ThemesClient.ACTOR);
  }

  /** Show `theme`: the live value, the host backend, the stored name, and a `ThemeChanged` as `actor`. */
  #show(theme: Option<ThemesClient.Theme>, actor: Actor): void {
    this.#current.update(theme);
    this.#host.themeBackend?.apply(theme);
    writeThemeName(this.#preferences, theme?.name ?? null);
    this.#invalidation.emit(new ThemeChanged(actor));
  }

  /** The server accepted `setDurable` number `issue` of `theme`. */
  #accept(theme: Option<ThemesClient.Theme>, issue: number): void {
    if (issue < this.#confirmedIssue) return;
    this.#confirmedIssue = issue;
    this.#confirmed = theme;
  }

  register(theme: ThemesClient.Theme): void {
    if (theme.name) this.#cache.set(theme.name, theme);
  }

  /**
   * The theme `name` if `get(name)` fetched it (or `register` held it),
   * else `null`: a synchronous read of the fetched cache, never a request —
   * `get(name)` loads it.
   */
  peek(name: string): Option<ThemesClient.Theme> {
    return this.#cache.get(name) ?? null;
  }

  async get(name: string): Promise<Option<ThemesClient.Theme>> {
    this.#lifecycle.assertLive("themes.get");
    const cached = this.#cache.get(name);
    if (cached) return cached;
    try {
      const themeData = await this.#resources.json(
        `/@/static/themes/${name}.json`,
        Network.Theme.parse,
      );
      const theme = ThemesClient.Theme.of({ name, theme: themeData });
      this.#cache.set(name, theme);
      return theme;
    } catch (err) {
      // Unknown or unreadable theme: the caller falls back to its default.
      Tracing.warn("ui")("theme-store/unresolvable", { name, error: String(err) });
      return null;
    }
  }

  /**
   * The built-in themes: `all()` (a fresh copy of the list) and `find(id)`.
   * A frozen face, the same object on every read.
   */
  get builtin(): {
    readonly all: () => ThemesClient.Named[];
    readonly find: (id: string) => Option<ThemesClient.Named>;
  } {
    return this.#builtin;
  }

  readonly #builtin = Object.freeze({
    all: (): ThemesClient.Named[] => [...BUILTIN_THEMES],
    find: (id: string): Option<ThemesClient.Named> =>
      BUILTIN_THEMES.find((t) => t.id === id) ?? null,
  });
}

/**
 * Types and values that go with `ThemesClient`: `Persist`, `persistDurable`,
 * `Theme`, `Backend`, `Name`, `Named`, and 2 more.
 */
export namespace ThemesClient {
  /**
   * The server write of a chosen theme (`null` for no theme): resolves once
   * the server accepted it, rejects with `SdkRequestError` when it refused
   * the write or never answered it.
   */
  export type Persist = (theme: Option<Theme>) => Promise<void>;

  /** The debounce key of the user's theme write: one key, so rapid choices coalesce to the last. */
  const THEME_KEY = "user:themes";

  /**
   * The client's theme `Persist`: `PATCH /api/user/themes` handed to the
   * client's debounce executor under one key (rapid choices coalesce to the
   * last), resolving once the execution that carried it answered without
   * refusing it and rejecting with `SdkRequestError` (boundary `user`, step
   * `update`; `outcome` per `DurableHandoffs.verdict`) when a refusal of it
   * reached `durable` first.
   */
  export function persistDurable(
    api: TypedApi<Network.Routes>,
    durable: DurableHandoffs,
  ): Persist {
    return async (theme) => {
      const requestId = durable.nextRequestId();
      const failure = await durable.handOff(THEME_KEY, requestId, () =>
        api.debounce.patch("/api/user/themes", {
          body: theme?.asPatch() ?? null,
          options: { key: THEME_KEY, requestId },
        }));
      if (failure == null) return;
      throw new SdkRequestError({
        boundary: "user",
        step: "update",
        method: "PATCH",
        path: "/api/user/themes",
        failure: DurableHandoffs.verdict(failure),
        reason: failure.reason ?? "",
        details: failure.details ?? "",
      });
    };
  }

  export class Theme {
    /**
     * `inner` layered over `Theme.BASE`: a colour it does not name comes from
     * the base theme.
     */
    static of(inner: Network.NamedTheme): Theme {
      return new Theme(inner, Theme.BASE);
    }

    /** `inner` as a root theme: a colour it does not name has none. */
    static root(inner: Network.NamedTheme): Theme {
      return new Theme(inner, null);
    }

    static readonly BASE: Theme = Object.freeze(Theme.root(
      deepFrozen({
        name: "default",
        theme: {
          base: null,
          semantic: {
            default: {
              sheet: {},
              events: {
                work: "var(--red)",
                social: "var(--teal)",
                exercise: "var(--yellow)",
                transportation: "var(--gray)",
                food: "var(--blue)",
                shower: "var(--blue)",
                errands: "var(--green)",
                nap: "var(--orange)",
                creative: "var(--purple)",
                break: "var(--purple)",
              },
            },
            dimmed: {
              sheet: {},
              events: {
                work: "var(--red-dimmed)",
                social: "var(--teal-dimmed)",
                exercise: "var(--yellow-dimmed)",
                transportation: "var(--gray-dimmed)",
                food: "var(--blue-dimmed)",
                shower: "var(--blue-dimmed)",
                errands: "var(--green-dimmed)",
                nap: "var(--orange-dimmed)",
                creative: "var(--purple-dimmed)",
                break: "var(--purple-dimmed)",
              },
            },
          },
        },
      }),
    ));

    /** `inner` over `base` (`null`: a root theme); see `Theme.of` / `Theme.root`. */
    constructor(
      readonly inner: Network.NamedTheme,
      readonly base: Option<Theme>,
    ) {}

    get name(): Option<string> {
      return this.inner.name;
    }

    semanticColor(
      namespace: Namespace,
      key: Option<string>,
      appearance: "dark" | "light",
    ): Option<string> {
      if (!key) return null;
      const color = semanticColorOf(this.inner.theme?.semantic?.default?.[namespace] ?? null, key, appearance);
      if (!color) return this.base?.semanticColor(namespace, key, appearance) ?? null;
      return color;
    }

    semanticColorDimmed(
      namespace: Namespace,
      key: Option<string>,
      appearance: "dark" | "light",
    ): Option<string> {
      if (!key) return null;
      const color = semanticColorOf(this.inner.theme?.semantic?.dimmed?.[namespace] ?? null, key, appearance);
      if (!color) return this.base?.semanticColorDimmed(namespace, key, appearance) ?? null;
      return color;
    }

    asPatch(): Network.UserTheme {
      if (this.inner.name != null) {
        return { Alias: { alias: this.inner.name } };
      }
      return {
        Custom: nonnull(this.inner.theme, "ThemesClient.asPatch: custom theme"),
      };
    }

    asBaseCssString(): string {
      const defaultColorMap = parseColorVariant(this.inner?.theme?.base?.default ?? null);
      const dimmedColorMap = parseColorVariant(this.inner?.theme?.base?.dimmed ?? null);

      const baseDefault = Object.entries(defaultColorMap).map(
        ([color, [light, dark]]) =>
          light === dark
            ? `--${color}: ${light};`
            : `--${color}: light-dark(${light}, ${dark});`,
      );
      const baseDimmed = Object.entries(dimmedColorMap).map(
        ([color, [light, dark]]) =>
          light === dark
            ? `--${color}-dimmed: ${light};`
            : `--${color}-dimmed: light-dark(${light}, ${dark});`,
      );
      const baseForeground =
        this.inner.name !== "default" && defaultColorMap.purple != null
          ? ["--purple-fg: var(--purple);"]
          : [];

      return `html,\nbody,\n:root {\n  ${baseDefault.join("\n  ")}\n  ${baseForeground.join("\n  ")}\n\n  ${baseDimmed.join("\n  ")}\n}`;
    }
  }

  export interface Backend {
    apply(theme: Option<Theme>): void;
  }

  export type Name = string & { readonly __themeId: string };

  /** A theme's name from its text (`Name.of("grass")`). */
  export namespace Name {
    /** `raw` as a theme `Name`, or `null` when it is not one (an empty string). */
    export function parse(raw: string): Option<Name> {
      return raw.length > 0 ? (raw as Name) : null;
    }

    /** `raw` as a theme `Name`; throws a `TypeError` when `parse` refuses it. */
    export function of(raw: string): Name {
      const name = parse(raw);
      if (name == null) throw new TypeError(`ThemesClient.Name.of: not a theme name: ${JSON.stringify(raw)}`);
      return name;
    }
  }
  export type Named = { label: string; id: Name; preview: string };
  export type ColorMap = { [key: string]: [string, string] };
  export type Namespace = "events" | "sheet";
}

/**
 * `key`'s colour in one semantic namespace table (`{ [key]: colour }`, where a
 * colour is a string or a `[light, dark]` pair), for `appearance`; `null` when
 * the table does not name `key` with a string colour.
 */
function semanticColorOf(
  table: Option<JsonObject>,
  key: string,
  appearance: "dark" | "light",
): Option<string> {
  const color = table?.[key];
  if (typeof color === "string") return color;
  if (!Array.isArray(color)) return null;
  const picked = color.length === 2 && appearance === "dark" ? color[1] : color[0];
  return typeof picked === "string" ? picked : null;
}

function parseColorVariant(colorVariant: Option<Network.CoreTheme>): ThemesClient.ColorMap {
  if (!colorVariant) return {};
  const result: ThemesClient.ColorMap = {};
  for (const key of ["red", "orange", "yellow", "green", "blue", "purple", "gray", "teal"]) {
    const color = colorVariant[key as keyof Network.CoreTheme];
    if (!color) {
      result[key] = [`--sys-${key}`, `--sys-${key}-dimmed`];
    } else if (typeof color === "string") {
      result[key] = [color, color];
    } else if (Array.isArray(color)) {
      const [light, dark] = color;
      if (light != null && dark != null) result[key] = [light, dark];
    }
  }
  return result;
}

/** The built-in themes, frozen (each entry too): `builtin.all()` copies the list. */
const BUILTIN_THEMES: readonly ThemesClient.Named[] = Object.freeze([
  { label: "Default", id: ThemesClient.Name.of("default"), preview: "linear-gradient(135deg, var(--yellow) 25%, var(--blue) 25%, var(--green) 50%, var(--red) 50%, var(--red) 75%, var(--purple) 75%)" },
  { label: "Grass", id: ThemesClient.Name.of("grass"), preview: "linear-gradient(45deg, #f8e7dc 25%, #c7d5ba 25%, #c7d5ba 50%, #f2bab9 50%, #f2bab9 75%, #f8e7dc 75%)" },
  { label: "Solarized", id: ThemesClient.Name.of("solarized"), preview: "linear-gradient(90deg, #5c8eb7 33%, #5aada5 33%, #5aada5 66%, #8789b0 66%)" },
  { label: "Bubblegum", id: ThemesClient.Name.of("bubblegum"), preview: "linear-gradient(to right, #ff85ea 33%, #85c1ff 33%, #85c1ff 66%, #a5ff85 66%)" },
  { label: "Fall", id: ThemesClient.Name.of("fall"), preview: "linear-gradient(to bottom, #a67c59 33%, #cba642 33%, #cba642 66%, #7d9158 66%)" },
  { label: "Sky", id: ThemesClient.Name.of("sky"), preview: "linear-gradient(60deg, #87ceeb 25%, #4682b4 25%, #4682b4 50%, #1e90ff 50%, #1e90ff 75%, #87ceeb 75%)" },
  { label: "Lofi", id: ThemesClient.Name.of("lofi"), preview: "linear-gradient(120deg, #d8a9a3 25%, #e3d9b2 25%, #e3d9b2 50%, #a1b5d8 50%, #a1b5d8 75%, #cbc0d3 75%)" },
].map((theme) => Object.freeze(theme)));
